import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Star, MapPin, Clock, Languages, Award, ShieldCheck, Phone, Calendar } from "lucide-react";
import { api, formatINR } from "@/lib/api";

export default function DoctorProfile() {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get(`/doctors/${id}`).then((r) => setDoctor(r.data)).catch(() => setDoctor(false));
    api.get(`/doctors/${id}/reviews`).then((r) => setReviews(r.data));
  }, [id]);

  if (doctor === null) return <div className="p-20 text-center text-slate-500">Loading…</div>;
  if (doctor === false) return <div className="p-20 text-center text-slate-500">Doctor not found.</div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-blue-700 to-teal-600 p-6 text-white sm:p-10">
        <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-end">
          <img
            src={doctor.photo_url}
            alt={doctor.name}
            className="h-28 w-28 rounded-2xl border-4 border-white object-cover sm:h-36 sm:w-36"
            onError={(e) => { e.target.src = `https://ui-avatars.com/api/?background=fff&color=1E40AF&name=${encodeURIComponent(doctor.name)}&size=300`; }}
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs font-bold uppercase tracking-widest">Verified Doctor</span>
            </div>
            <h1 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-4xl">{doctor.name}</h1>
            <div className="mt-1 text-sm font-medium text-blue-100">{doctor.qualification}</div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="font-semibold">{doctor.specialty}</span>
              <span className="text-blue-200">•</span>
              <span>{doctor.experience_years} years experience</span>
              <span className="text-blue-200">•</span>
              <div className="flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{doctor.rating?.toFixed(1) || "—"} ({doctor.reviews_count || 0})</div>
            </div>
          </div>
          <div className="rounded-2xl bg-white/10 p-4 text-right backdrop-blur">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-100">Consultation Fee</div>
            <div className="mt-1 text-3xl font-extrabold">{formatINR(doctor.consultation_fee)}</div>
            <Link to={`/book/${doctor.id}`} data-testid="profile-book-btn" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-blue-700 shadow-sm hover:bg-blue-50">
              <Calendar className="h-4 w-4" /> Book Appointment
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-2 border-b border-slate-200">
        {[
          { id: "overview", label: "Overview" },
          { id: "reviews", label: `Reviews (${reviews.length})` },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`profile-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`relative px-4 py-3 text-sm font-semibold transition-colors ${
              tab === t.id ? "text-blue-700" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
            {tab === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-blue-700" />}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
          <div className="space-y-6">
            <div className="card p-6">
              <h3 className="text-lg font-bold text-slate-900">About the doctor</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{doctor.bio || "This doctor hasn't added a bio yet."}</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <Award className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Qualifications</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{doctor.qualification}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <Clock className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Experience</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{doctor.experience_years} years</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <Languages className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Languages</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{doctor.languages?.join(", ") || "English"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <Clock className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Working Hours</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {doctor.working_hours?.start} – {doctor.working_hours?.end}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="card p-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-teal-700">Clinic</div>
              <div className="mt-2 text-lg font-bold text-slate-900">{doctor.clinic_name}</div>
              <div className="mt-3 flex items-start gap-2 text-sm text-slate-700">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <span>{doctor.clinic_address}</span>
              </div>
              <div className="mt-5 h-40 overflow-hidden rounded-xl border border-slate-200">
                <iframe
                  title="Clinic map"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(doctor.clinic_address)}&output=embed`}
                  className="h-full w-full"
                />
              </div>
              <Link to={`/book/${doctor.id}`} className="btn-primary mt-5 w-full">Book Slot</Link>
            </div>
          </aside>
        </div>
      )}

      {tab === "reviews" && (
        <div className="mt-8 space-y-4">
          {reviews.length === 0 && <div className="card p-10 text-center text-sm text-slate-500">No reviews yet. Be the first to review after your visit.</div>}
          {reviews.map((r) => (
            <div key={r.id} className="card p-5">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{r.patient_name}</div>
                <div className="flex items-center gap-1 text-amber-500">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-current" : "text-slate-200"}`} />)}
                </div>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">{r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
